import React from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { getDateDescription } from 'flow/utils';

interface Props {
    date: string;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const DateDivider = ({ date, videoDark = false }: Props) => {
    const { i18next } = useAppContext();

    return (
        // The offset cancels the padding below it, so the pill pins flush with
        // the top of the scroller rather than a gap under it, and the backdrop
        // keeps messages from showing through as they scroll beneath.
        <div
            data-test-id="message-group-divider"
            className="sticky -top-6 z-10 w-full"
        >
            {/* Three-quarters, so the band laid over the messages is no taller
                than it has to be. Full height was needed while every day's pill
                pinned at the same offset and one had to mask the last; each day
                is now its own box, so nothing is stuck behind this. */}
            <div
                className={twMerge(
                    'absolute h-3/4 w-full',
                    videoDark ? 'bg-gray-800' : 'bg-white',
                )}
            />
            <div className="relative w-full px-4 pt-6">
                <div className="relative w-full">
                    <div
                        className={twMerge(
                            'absolute start-0 top-1/2 z-0 h-[1px] w-full',
                            videoDark ? 'bg-gray-600' : 'bg-gray-300',
                        )}
                    />
                    <div className="flex w-full justify-center">
                        <div
                            className={twMerge(
                                'relative flex h-6 items-center rounded-sm border px-3 py-0.5',
                                videoDark
                                    ? 'border-gray-600 bg-gray-800'
                                    : 'border-gray-300 bg-white',
                            )}
                        >
                            <span
                                className={twMerge(
                                    'text-xs font-bold',
                                    videoDark
                                        ? 'text-gray-300'
                                        : 'text-gray-700',
                                )}
                            >
                                {getDateDescription(date, i18next)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(DateDivider);
